import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ShoppingList = ({ shoppingList = [], participants = [], onUpdate }) => {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    name: '',
    quantity: 1,
    unit: 'un',
    category: 'meat',
    priority: 'medium',
    assignedTo: '',
    purchased: false,
    price: 0
  });

  const [suppliers, setSuppliers] = useState([
    {
      id: 1,
      name: 'Açougue do João',
      type: 'meat',
      phone: '(11) 9999-8888',
      address: 'Rua das Carnes, 123',
      rating: 4.8,
      delivery: true
    },
    {
      id: 2,
      name: 'Bebidas Express',
      type: 'beverages',
      phone: '(11) 8888-7777',
      address: 'Av. das Bebidas, 456',
      rating: 4.5,
      delivery: true
    }
  ]);

  const [selectedSuppliers, setSelectedSuppliers] = useState({});
  const [showSuppliers, setShowSuppliers] = useState(false);

  useEffect(() => {
    setItems(shoppingList);
  }, [shoppingList]);

  const categories = [
    { value: 'meat', label: 'Carnes', icon: 'Beef' },
    { value: 'beverages', label: 'Bebidas', icon: 'Coffee' },
    { value: 'sides', label: 'Acompanhamentos', icon: 'Salad' },
    { value: 'equipment', label: 'Equipamentos', icon: 'Wrench' },
    { value: 'other', label: 'Outros', icon: 'Package' }
  ];

  const priorities = [
    { value: 'high', label: 'Alta', color: 'text-red-600' },
    { value: 'medium', label: 'Média', color: 'text-yellow-600' },
    { value: 'low', label: 'Baixa', color: 'text-green-600' }
  ];

  const units = ['un', 'kg', 'g', 'L', 'ml', 'cx', 'pct'];

  const addItem = () => {
    if (!newItem?.name?.trim()) return;
    
    const item = {
      id: Date.now(),
      ...newItem,
      name: newItem?.name?.trim()
    };
    
    const updated = [...items, item];
    setItems(updated);
    onUpdate(updated);
    
    setNewItem({
      name: '',
      quantity: 1,
      unit: 'un',
      category: 'meat',
      priority: 'medium',
      assignedTo: '',
      purchased: false,
      price: 0
    });
  };

  const updateItem = (id, field, value) => {
    const updated = items?.map(item => 
      item?.id === id ? { ...item, [field]: value } : item
    );
    setItems(updated);
    onUpdate(updated);
  };

  const removeItem = (id) => {
    const updated = items?.filter(item => item?.id !== id);
    setItems(updated);
    onUpdate(updated);
  };

  const generateSuggestedList = () => {
    const participantCount = participants?.length || 1;
    
    const suggested = [
      { name: 'Picanha', quantity: participantCount * 0.2, unit: 'kg', category: 'meat', priority: 'high' },
      { name: 'Linguiça', quantity: participantCount * 0.1, unit: 'kg', category: 'meat', priority: 'medium' },
      { name: 'Cerveja', quantity: participantCount * 3, unit: 'un', category: 'beverages', priority: 'high' },
      { name: 'Refrigerante', quantity: participantCount * 1, unit: 'un', category: 'beverages', priority: 'medium' },
      { name: 'Pão de Alho', quantity: Math.ceil(participantCount / 2), unit: 'un', category: 'sides', priority: 'medium' },
      { name: 'Carvão', quantity: 2, unit: 'kg', category: 'equipment', priority: 'high' },
      { name: 'Descartáveis', quantity: 1, unit: 'pct', category: 'equipment', priority: 'medium' }
    ];

    const newItems = suggested?.map((item, index) => ({
      id: Date.now() + index,
      ...item,
      assignedTo: '',
      purchased: false,
      price: 0
    }));

    const updated = [...items, ...newItems];
    setItems(updated);
    onUpdate(updated);
  };

  const assignItemsAutomatically = () => {
    if (participants?.length === 0) return;
    
    const updated = items?.map((item, index) => ({
      ...item,
      assignedTo: participants?.[index % participants?.length]?.name || ''
    }));
    
    setItems(updated);
    onUpdate(updated);
  };

  const getCategoryIcon = (category) => {
    const cat = categories?.find(c => c?.value === category);
    return cat?.icon || 'Package';
  };

  const getPriorityColor = (priority) => {
    const pri = priorities?.find(p => p?.value === priority);
    return pri?.color || 'text-gray-600';
  };

  const completedCount = items?.filter(item => item?.purchased)?.length;
  const totalValue = items?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-primary/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="ShoppingCart" size={20} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Total Itens</p>
              <p className="text-xl font-bold text-primary">{items?.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-success/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div>
              <p className="text-sm text-muted-foreground">Comprados</p>
              <p className="text-xl font-bold text-success">{completedCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-warning/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={20} className="text-warning" />
            <div>
              <p className="text-sm text-muted-foreground">Pendentes</p>
              <p className="text-xl font-bold text-warning">{items?.length - completedCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="DollarSign" size={20} className="text-secondary" />
            <div>
              <p className="text-sm text-muted-foreground">Valor Total</p>
              <p className="text-xl font-bold text-secondary">R$ {totalValue?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={generateSuggestedList}
          iconName="Lightbulb"
          disabled={participants?.length === 0}
        >
          Gerar Lista Sugerida
        </Button>
        
        <Button
          variant="outline"
          onClick={assignItemsAutomatically}
          iconName="Users"
          disabled={participants?.length === 0 || items?.length === 0}
        >
          Distribuir Automaticamente
        </Button>
        
        <Button
          variant="outline"
          onClick={() => setShowSuppliers(!showSuppliers)}
          iconName="Store"
        >
          Fornecedores
        </Button>
      </div>
      {/* Suppliers */}
      {showSuppliers && (
        <div className="bg-card rounded-lg border border-border p-4">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Icon name="Store" size={18} />
            Fornecedores Recomendados
          </h3>
          
          <div className="grid gap-3">
            {suppliers?.map((supplier) => (
              <div key={supplier?.id} className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-foreground">{supplier?.name}</h4>
                      <div className="flex items-center gap-1 text-yellow-500">
                        <Icon name="Star" size={12} fill="currentColor" />
                        <span className="text-xs">{supplier?.rating}</span>
                      </div>
                      {supplier?.delivery && (
                        <span className="text-xs bg-success text-success-foreground px-2 py-1 rounded">
                          Entrega
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{supplier?.address}</p>
                    <p className="text-sm text-muted-foreground">{supplier?.phone}</p>
                  </div>
                  
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`tel:${supplier?.phone}`)}
                      iconName="Phone"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const message = `Olá! Gostaria de fazer um pedido para churrasco de ${participants?.length} pessoas.`;
                        window.open(`https://wa.me/${supplier?.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`);
                      }}
                      iconName="MessageCircle"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Add New Item */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h3 className="font-medium text-foreground mb-4">Adicionar Item</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <Input
            label="Nome do Item"
            value={newItem?.name}
            onChange={(e) => setNewItem(prev => ({ ...prev, name: e?.target?.value }))}
            placeholder="Ex: Picanha"
          />
          
          <div className="grid grid-cols-2 gap-2">
            <Input
              label="Quantidade"
              type="number"
              step="0.1"
              value={newItem?.quantity}
              onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseFloat(e?.target?.value) || 0 }))}
            />
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Unidade</label>
              <select
                value={newItem?.unit}
                onChange={(e) => setNewItem(prev => ({ ...prev, unit: e?.target?.value }))}
                className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {units?.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Categoria</label>
            <select
              value={newItem?.category}
              onChange={(e) => setNewItem(prev => ({ ...prev, category: e?.target?.value }))}
              className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {categories?.map(cat => (
                <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Responsável</label>
            <select
              value={newItem?.assignedTo}
              onChange={(e) => setNewItem(prev => ({ ...prev, assignedTo: e?.target?.value }))}
              className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Selecionar...</option>
              {participants?.map(p => (
                <option key={p?.id} value={p?.name}>{p?.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        <Button
          onClick={addItem}
          disabled={!newItem?.name?.trim()}
          iconName="Plus"
          iconPosition="left"
        >
          Adicionar à Lista
        </Button>
      </div>
      {/* Shopping List */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground">Lista de Compras ({items?.length})</h3>
        
        {items?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="ShoppingCart" size={48} className="mx-auto mb-2 opacity-50" />
            <p>Nenhum item na lista ainda</p>
            <p className="text-sm">Adicione itens ou use a lista sugerida</p>
          </div>
        ) : (
          <div className="space-y-2">
            {categories?.map((category) => {
              const categoryItems = items?.filter(item => item?.category === category?.value);
              if (categoryItems?.length === 0) return null;
              
              return (
                <div key={category?.value} className="bg-card rounded-lg border border-border">
                  <div className="p-3 border-b border-border bg-muted/30">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      <Icon name={category?.icon} size={16} />
                      {category?.label} ({categoryItems?.length})
                    </h4>
                  </div>
                  <div className="p-3 space-y-2">
                    {categoryItems?.map((item) => (
                      <div
                        key={item?.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          item?.purchased 
                            ? 'bg-success/5 border-success/20' :'bg-background border-border hover:bg-muted/30'
                        }`}
                      >
                        <button
                          onClick={() => updateItem(item?.id, 'purchased', !item?.purchased)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            item?.purchased
                              ? 'bg-success border-success text-success-foreground'
                              : 'border-border hover:border-primary'
                          }`}
                        >
                          {item?.purchased && <Icon name="Check" size={12} />}
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${item?.purchased ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                              {item?.name}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {item?.quantity} {item?.unit}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(item?.priority)} bg-current/10`}>
                              {priorities?.find(p => p?.value === item?.priority)?.label}
                            </span>
                          </div>
                          
                          {item?.assignedTo && (
                            <p className="text-sm text-muted-foreground">
                              Responsável: {item?.assignedTo}
                            </p>
                          )}
                          
                          {item?.price > 0 && (
                            <p className="text-sm text-muted-foreground">
                              R$ {(item?.price * item?.quantity)?.toFixed(2)}
                            </p>
                          )}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(item?.id)}
                          className="text-destructive hover:bg-destructive/10"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Export Options */}
      {items?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const listText = items
                ?.map(item => `${item?.purchased ? '✓' : '□'} ${item?.name} - ${item?.quantity}${item?.unit}${item?.assignedTo ? ` (${item?.assignedTo})` : ''}`)
                ?.join('\n');
              navigator.clipboard?.writeText(listText);
              alert('Lista copiada!');
            }}
            iconName="Copy"
          >
            Copiar Lista
          </Button>
          
          <Button
            variant="outline"
            onClick={() => {
              const pendingItems = items?.filter(item => !item?.purchased && item?.assignedTo);
              const assignments = {};
              
              pendingItems?.forEach(item => {
                if (!assignments?.[item?.assignedTo]) {
                  assignments[item.assignedTo] = [];
                }
                assignments?.[item?.assignedTo]?.push(`${item?.name} - ${item?.quantity}${item?.unit}`);
              });
              
              const message = Object.entries(assignments)
                ?.map(([person, items]) => `${person}:\n${items?.join('\n')}`)
                ?.join('\n\n');
                
              navigator.clipboard?.writeText(message);
              alert('Responsabilidades copiadas!');
            }}
            iconName="Users"
            disabled={!items?.some(item => !item?.purchased && item?.assignedTo)}
          >
            Copiar Responsabilidades
          </Button>
        </div>
      )}
    </div>
  );
};

export default ShoppingList;